// KAN-709 (RD-LUI-004) — the ONE place the platform home lists are invoked and
// post-processed. Used by usePlatformHomeData (mount + refresh) AND by the
// sign-in prefetch (`prefetchPlatformHome` in platformSessionStore), so the two
// can never drift. Memory-only; the prefetch entry is single-use, per user,
// epoch-checked and ignored once older than the caller's max age.
// `supabase` is imported lazily so this module stays importable in Jest.
import { withLastSignInFlag } from '../team/lastSignInPayload';

const loadSupabase = async () => (await import('../../supabase')).supabase;

/** Outcome: `{ data, error, thrown }` — `data` is post-processed (users carry the last-sign-in flag). */
export async function fetchPlatformTenants() {
  try {
    const supabase = await loadSupabase();
    const { data, error } = await supabase.functions.invoke('platform-list-tenants');
    return { data, error, thrown: false };
  } catch (_) {
    return { data: null, error: null, thrown: true };
  }
}

export async function fetchPlatformUsers() {
  try {
    const supabase = await loadSupabase();
    const { data, error } = await supabase.functions.invoke('platform-list-platform-users');
    const out = { data, error, thrown: false };
    if (!error && data?.success) out.users = withLastSignInFlag(data.platform_users, data);
    return out;
  } catch (_) {
    return { data: null, error: null, thrown: true };
  }
}

// ── Sign-in prefetch entry ─────────────────────────────────────────────────
// Mirrors SIGN_IN_PREFETCH_MAX_AGE_MS (signInPrefetch.js — not imported: it pulls the
// eager supabase client). A parity test pins the two together.
export const PLATFORM_HOME_PREFETCH_MAX_AGE_MS = 15000;
let entry = null; // { userId, epoch, startedAt, tenants, users, done, timer }

function dropEntry() {
  if (entry) clearTimeout(entry.timer);
  entry = null;
}

/** Starts (or joins) the prefetch. `isCurrent()` = no sign-out since start. Never rejects. */
export function runPlatformHomePrefetch(userId, epoch, isCurrent) {
  if (!userId) return Promise.resolve();
  // Dedupe only while in flight; a settled entry waits for take / its age check.
  if (entry && entry.userId === userId && entry.epoch === epoch && !entry.settled) return entry.done;
  dropEntry(); // a replaced entry's timer never outlives it
  const mine = {
    userId, epoch, startedAt: Date.now(),
    tenants: fetchPlatformTenants(),
    users: fetchPlatformUsers(),
  };
  mine.done = Promise.all([mine.tenants, mine.users]).then(() => {
    mine.settled = true;
    // Sign-out mid-flight: drop the entry (clear already nulled it; guard anyway).
    if (!isCurrent() && entry === mine) entry = null;
  }, () => {});
  entry = mine;
  // An untaken entry drops itself after the max age (only if it is still THIS entry).
  mine.timer = setTimeout(() => { if (entry === mine) entry = null; }, PLATFORM_HOME_PREFETCH_MAX_AGE_MS);
  return mine.done;
}

/** Single-use: `{ tenants, users }` promises of outcomes, or null. Clears the entry. */
// `maxAgeMs` is passed by the caller (SIGN_IN_PREFETCH_MAX_AGE_MS) so this module
// never imports signInPrefetch.js, which pulls the eager supabase client.
export function takePlatformHomePrefetch(userId, currentEpoch, maxAgeMs) {
  const e = entry;
  if (!e || !userId || e.userId !== userId) return null;
  dropEntry();
  if (e.epoch !== currentEpoch) return null;
  if (Date.now() - e.startedAt >= maxAgeMs) return null;
  return { tenants: e.tenants, users: e.users };
}

export function clearPlatformHomePrefetch() {
  dropEntry();
}
